import { BusinessSubscriptionModel, billingApi } from '@pukka/api';
import { clickstreamTracker } from '@pukka/clickstream';
import { DeepReadonly } from '@pukka/common/types';
import { assertUtil } from '@pukka/common/utils';
import { toast } from '@pukka/toast';
import { proxy } from 'valtio';

import { BillingPersistedState } from '../Billing.state';

interface IRecurringPaymentState extends Record<string, unknown> {
  isPriceBreakdownModalShown: boolean;
  isModifyBillingModalShown: boolean;
  isLoadingManageCard: boolean;
  isChangingPackage: boolean;
  onTogglePriceBreakdownModal(): void;
  onToggleModifyBillingModal(): void;
  onManageCard(getter?: (typeof billingApi)['subscribeRecurringPayment']): Promise<void>;
  onChangePackage(
    data: DeepReadonly<BusinessSubscriptionModel.BillingCalculation>,
    getter?: (typeof billingApi)['subscribeRecurringPayment'],
  ): Promise<void>;
}

const RecurringPaymentState: IRecurringPaymentState = proxy({
  isPriceBreakdownModalShown: false,
  isModifyBillingModalShown: false,
  isLoadingManageCard: false,
  isChangingPackage: false,
  onTogglePriceBreakdownModal() {
    RecurringPaymentState.isPriceBreakdownModalShown = !RecurringPaymentState.isPriceBreakdownModalShown;
  },
  onToggleModifyBillingModal() {
    RecurringPaymentState.isModifyBillingModalShown = !RecurringPaymentState.isModifyBillingModalShown;
  },
  async onManageCard(getter = billingApi.subscribeRecurringPayment) {
    RecurringPaymentState.isLoadingManageCard = true;

    const isCreditCard = BillingPersistedState.recurring?.payment.type === 'credit_card';

    // TODO: add tracker for credit card later
    if (!isCreditCard) {
      clickstreamTracker.trackComponent('MOKAPOS_MODIFY_DEBIT_INFO');
    }

    try {
      const { url } = await getter({
        payment_type: isCreditCard ? 'CREDIT_CARD' : 'DEBIT',
        final_amount: BillingPersistedState.recurring?.prices.total.finalPrice,
        coupon_code: BillingPersistedState.recurring?.prices.coupon.code,
      });

      if (url) {
        window.location.href = url;
      }
    } catch (error: unknown) {
      assertUtil.assertObjectIsTruthy<{
        data:
          | string
          | {
              error?: string;
              status: string;
            };
        status: number;
      }>(error);

      toast.error(typeof error.data === 'string' ? error.data : error.data.error ?? error.data.status);
    } finally {
      RecurringPaymentState.isLoadingManageCard = false;
    }
  },
  async onChangePackage(data, getter = billingApi.subscribeRecurringPayment) {
    RecurringPaymentState.isChangingPackage = true;

    try {
      const { url } = await getter({
        subscription_discount_id: data.subscription_discount_id,
      });

      if (url) {
        window.location.href = url;
      }
    } catch (error: unknown) {
      assertUtil.assertObjectIsTruthy<{
        data:
          | string
          | {
              error?: string;
              status: string;
            };
        status: number;
      }>(error);

      toast.error(typeof error.data === 'string' ? error.data : error.data.error ?? error.data.status);
    } finally {
      RecurringPaymentState.isChangingPackage = false;
    }
  },
});

export { RecurringPaymentState };
